/**
 * NotificationSettingsPanel — in-drawer settings (master toggle, per-category
 * mute toggles, push enable/disable, send-test button, sound preference).
 *
 * Persists prefs to the backend via `/api/notifications/preferences`
 * (existing endpoint).  Sound + drawer-only prefs persist to localStorage
 * since they're client-only.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import * as Phosphor from '@phosphor-icons/react';
import { BellRinging, BellSlash, PaperPlaneTilt, SpeakerHigh, SpeakerSlash } from '@phosphor-icons/react';
import usePushNotifications from '../hooks/usePushNotifications';
import { useNotifications } from './NotificationProvider';

const API = process.env.REACT_APP_BACKEND_URL;
const SOUND_KEY = 'cs-notif-sound-v1';

const NotificationSettingsPanel = () => {
  const { categories } = useNotifications();
  const { supported, permission, subscribed, loading, enable, disable, sendTest } = usePushNotifications();
  const [prefs, setPrefs] = useState({});
  const [master, setMaster] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [sound, setSound] = useState(() => localStorage.getItem(SOUND_KEY) === '1');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/notifications/preferences`, { withCredentials: true });
        setPrefs(data?.prefs || {});
        setMaster(data?.master !== false);
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const save = async (nextPrefs, nextMaster) => {
    try {
      await axios.put(`${API}/api/notifications/preferences`,
        { prefs: nextPrefs, master: nextMaster },
        { withCredentials: true });
    } catch {
      toast.error('Failed to save preferences');
    }
  };

  const togglePref = (key) => {
    const next = { ...prefs, [key]: prefs[key] === false ? true : false };
    setPrefs(next);
    save(next, master);
  };

  const toggleMaster = () => {
    const m = !master;
    setMaster(m);
    save(prefs, m);
    toast.success(m ? 'Notifications enabled' : 'Notifications muted');
  };

  const toggleSound = () => {
    const s = !sound;
    setSound(s);
    localStorage.setItem(SOUND_KEY, s ? '1' : '0');
  };

  const handlePush = async () => {
    try {
      if (subscribed) {
        await disable();
        toast.success('Push disabled on this device');
      } else {
        await enable();
        toast.success('Push enabled on this device');
      }
    } catch (e) {
      toast.error(e?.message || 'Could not toggle push');
    }
  };

  const handleTest = async () => {
    try {
      await sendTest();
      toast.success('Test sent — check your notifications');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send test');
    }
  };

  const Row = ({ icon: Icon, accent, title, desc, active, onToggle, testId }) => (
    <button
      type="button"
      onClick={onToggle}
      data-testid={testId}
      className="w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left"
      style={{ background: 'var(--cs-bg-surface)', border: '1px solid var(--cs-border)' }}
    >
      <div className="w-9 h-9 rounded-lg shrink-0 flex items-center justify-center"
        style={{ background: `${accent}22`, border: `1px solid ${accent}33` }}>
        <Icon size={16} weight="fill" style={{ color: accent }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold" style={{ color: 'var(--cs-text)' }}>{title}</div>
        <div className="text-[10.5px]" style={{ color: 'var(--cs-text-muted)' }}>{desc}</div>
      </div>
      <span
        className="w-9 h-5 rounded-full relative shrink-0 transition-colors"
        style={{ background: active ? accent : 'var(--cs-bg-hover)' }}>
        <span
          className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all"
          style={{ left: active ? '18px' : '2px', boxShadow: '0 1px 3px rgba(0,0,0,0.4)' }}
        />
      </span>
    </button>
  );

  return (
    <div className="space-y-3" data-testid="notification-settings-panel">
      {/* Master switch */}
      <Row
        icon={master ? BellRinging : BellSlash}
        accent="var(--cs-notif-accent)"
        title="Notifications"
        desc={master ? 'You are receiving notifications' : 'All notifications muted'}
        active={master}
        onToggle={toggleMaster}
        testId="notif-master-toggle"
      />

      {/* Sound */}
      <Row
        icon={sound ? SpeakerHigh : SpeakerSlash}
        accent="#10B981"
        title="Sound on arrival"
        desc={sound ? 'A soft chime when a high-priority alert arrives' : 'Silent — visual only'}
        active={sound}
        onToggle={toggleSound}
        testId="notif-sound-toggle"
      />

      {/* Push */}
      {supported && (
        <div className="rounded-xl p-3 flex items-center gap-3"
          style={{ background: 'var(--cs-bg-surface)', border: '1px solid var(--cs-border)' }}>
          <div className="w-9 h-9 rounded-lg shrink-0 flex items-center justify-center"
            style={{ background: 'rgba(205,4,11,0.18)', border: '1px solid rgba(205,4,11,0.3)' }}>
            <BellRinging size={16} weight="fill" style={{ color: '#CD040B' }} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold" style={{ color: 'var(--cs-text)' }}>
              Push on this device
            </div>
            <div className="text-[10.5px]" style={{ color: 'var(--cs-text-muted)' }}>
              {subscribed ? 'Active for offline alerts' : 'Add this device for offline alerts'}
            </div>
          </div>
          <button
            type="button"
            onClick={handlePush}
            disabled={loading || permission === 'denied'}
            data-testid="notif-push-toggle"
            className="px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all"
            style={subscribed
              ? { background: 'var(--cs-bg-hover)', color: 'var(--cs-text-muted)' }
              : { background: 'linear-gradient(135deg,#CD040B 0%,#F59E0B 100%)', color: '#FFF' }}>
            {loading ? '…' : subscribed ? 'Off' : 'On'}
          </button>
        </div>
      )}

      {/* Send test */}
      {subscribed && (
        <button
          type="button"
          onClick={handleTest}
          data-testid="notif-send-test"
          className="w-full py-2.5 rounded-xl text-[12px] font-semibold flex items-center justify-center gap-2"
          style={{ background: 'var(--cs-bg-hover)', color: 'var(--cs-text)' }}>
          <PaperPlaneTilt size={13} weight="bold" />Send test
        </button>
      )}

      {/* Per-category toggles */}
      {loaded && Object.keys(categories).length > 0 && (
        <div className="pt-2">
          <div className="text-[10px] font-bold uppercase tracking-[1.5px] mb-2 pl-1"
            style={{ color: 'var(--cs-text-dim)' }}>Categories</div>
          <div className="space-y-1.5">
            {Object.entries(categories).map(([k, m]) => {
              const Icon = Phosphor[m.icon] || Phosphor.Gear;
              const active = master && (prefs[k] !== false);
              const disabled = !master;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => !disabled && togglePref(k)}
                  data-testid={`notif-cat-${k}`}
                  disabled={disabled}
                  className="w-full flex items-center gap-3 p-2.5 rounded-xl transition-all text-left"
                  style={{
                    background: 'var(--cs-bg-surface)',
                    border: '1px solid var(--cs-border)',
                    opacity: disabled ? 0.5 : 1,
                  }}>
                  <div className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center"
                    style={{ background: `${m.accent}1a`, border: `1px solid ${m.accent}33` }}>
                    <Icon size={14} weight="fill" style={{ color: m.accent }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] font-semibold" style={{ color: 'var(--cs-text)' }}>{m.label}</div>
                    <div className="text-[10px]" style={{ color: 'var(--cs-text-muted)' }}>{m.desc}</div>
                  </div>
                  <span
                    className="w-9 h-5 rounded-full relative shrink-0"
                    style={{ background: active ? m.accent : 'var(--cs-bg-hover)' }}>
                    <span
                      className="absolute top-0.5 w-4 h-4 rounded-full bg-white"
                      style={{ left: active ? '18px' : '2px', transition: 'left 160ms' }}
                    />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationSettingsPanel;
